import React, { useEffect, useState } from "react";
import {
  HeroLandingSliceFragment,
  PrismicReusableContent,
  PrismicReusableContentDataBodyImageGrid,
} from "../../types/graphql";
import {
  HeroBackground,
  HeroTitle,
  HeroText,
  HeroHeader,
  HeroSubtitle,
  CTACaption,
  CTAButton,
  CTA,
  StyledQuickFacts,
  HeroBackgroundFull,
  HeroBackgroundDesktop,
  HeroContentWrapper,
  HeroAction,
  HeroImageRight,
  HeroImageRightText,
  HeroImageRightImage,
  HeroImageRightAnimation,
  Certifications,
} from "./hero-landing.styled";
import { PrismicRichText } from "@prismicio/react";
import { htmlSerializer } from "../../utils/htmlSerializer";
import Breadcrumbs, { BreadCrumbProps } from "../breadcrumbs/breadcrumbs";
import { track } from "../../utils/analytics";
import CustomerQuoteRequest from "../customer-quote-request/customer-quote-request";
import * as FA from "../../styles/fontawesome";
import { SharedSliceType } from "../shared-slice/shared-slice";

import Lottie from "react-lottie";
import animationData from "./anim3.json";
import {
  IconCertificate,
  IconShieldCheck,
} from "../hero-bifurcated/hero-bifurcated-icons";
import TrustBox from "../trust-box/trust-box";

enum Layout {
  ImageRight = "image-right",
  ImageRight5050 = "image-right-50-50",
}

enum LinkedContentPlacement {
  SubTitle = "sub-title",
  Bottom = "bottom",
}

interface IHeroLandingProps {
  hero: HeroLandingSliceFragment;
  breadcrumbs?: BreadCrumbProps[];
}

const getLinkedContentItems = (linkedContent: PrismicReusableContent) => {
  let itemDocumentImageGrid =
    undefined as unknown as PrismicReusableContentDataBodyImageGrid;

  if (linkedContent?.data?.body?.length > 0) {
    itemDocumentImageGrid = linkedContent.data.body.filter(
      (body) => body.slice_type === SharedSliceType.image_grid
    )[0] as PrismicReusableContentDataBodyImageGrid;
  }

  return itemDocumentImageGrid;
};

const LandingHero = (props: IHeroLandingProps) => {
  track("Viewed RFQ Landing/Capabilities");
  var hero = props.hero.primary;
  const {
    rfq_cta_type,
    customer_rfq_prompt_text,
    customer_rfq_prompt_button_text,
    rfq_cta_section_title,
    linked_content_placement,
  } = hero;

  const showRfqCta: boolean = (rfq_cta_type &&
    rfq_cta_type.toLowerCase() !== "none") as boolean;
  const useStandaloneRfqCta: boolean = (rfq_cta_type &&
    rfq_cta_type.toLowerCase() === "rfq only") as boolean;

  const imageFitContain: boolean = hero.image_fit ? hero.image_fit : false;

  const companyLogos = getLinkedContentItems(
    hero.linked_content?.document as PrismicReusableContent
  );

  const breadcrumbs = props?.breadcrumbs;

  const lottie = props.hero.primary.lottie.richText;
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: lottie.length > 0 && JSON.parse(lottie[0].text),
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const HeroTextContent = () => {
    return (
      <HeroText
        className={
          hero.layout !== Layout.ImageRight &&
          hero.layout !== Layout.ImageRight5050 &&
          !showRfqCta
            ? `col-span-4 md-col-span-4 lg-col-span-7`
            : showRfqCta
            ? `col-span-4 md-col-span-4 lg-col-span-6`
            : ``
        }
      >
        <HeroHeader>
          {hero.title && <HeroTitle>{hero.title}</HeroTitle>}
          {companyLogos &&
            linked_content_placement === LinkedContentPlacement.SubTitle && (
              <div className={`${companyLogos.primary?.variant}`}>
                <CompanyLogos />
              </div>
            )}
          {hero.subtitle.richText.length > 0 && (
            <HeroSubtitle className="intro">
              <PrismicRichText
                field={hero.subtitle.richText}
                components={htmlSerializer}
              />
            </HeroSubtitle>
          )}
        </HeroHeader>
        {props.hero.items?.length > 0 && (
          <StyledQuickFacts quickFacts={props.hero.items} />
        )}
        {hero.cta_text && (
          <CTA>
            <CTAButton
              kind="primary"
              pageSection="landing-hero"
              ctaText={hero.cta_text}
              action={hero.cta_action}
              linkURL={hero.cta_link && hero.cta_link.url}
              target={hero.cta_link && hero.cta_link.target}
              driftBotInteractionID={hero.drift_bot_interaction_id}
            >
              {hero.cta_icon && hero.cta_icon === "upload" && (
                <>
                  <i css={FA.CloudArrowUpSolid} />
                  &nbsp;&nbsp;
                </>
              )}
            </CTAButton>
            {hero.cta_microcopy && (
              <CTACaption>
                <i css={FA.LockKeyholeSolid}></i>&nbsp;&nbsp;All uploads are
                secure and confidential.
              </CTACaption>
            )}
          </CTA>
        )}
      </HeroText>
    );
  };

  const HeroActionContent = () => {
    return (
      <HeroAction className="col-span-4 lg-col-span-6">
        <CustomerQuoteRequest
          buttonText={customer_rfq_prompt_button_text || undefined}
          prompt={customer_rfq_prompt_text || undefined}
          standAlone={useStandaloneRfqCta}
          rfqTitle={rfq_cta_section_title || undefined}
          compact
        />
      </HeroAction>
    );
  };

  const HeroContent = () => {
    return (
      <>
        <HeroTextContent />
        {showRfqCta && <HeroActionContent />}
      </>
    );
  };

  let textClassName = "";
  let imageClassName = "";

  switch (hero.layout) {
    case Layout.ImageRight:
      textClassName = "col-span-4 md-col-span-5 lg-col-span-8";
      imageClassName = "col-span-2 md-col-span-3 lg-col-span-4";
      break;
    case Layout.ImageRight5050:
      textClassName = "col-span-4 md-col-span-4 lg-col-span-6";
      imageClassName = "col-span-2 md-col-span-4 lg-col-span-6";
      break;
    default:
      textClassName = "";
      imageClassName = "";
      break;
  }

  const CompanyLogos = () => {
    return (
      <>
        {companyLogos && (
          <>
            <ul>
              {companyLogos.items?.map((item, index) => (
                <li key={index}>
                  <img
                    src={item.image?.url as string}
                    alt={item.image?.alt || "Xometry Image"}
                  />
                </li>
              ))}
            </ul>
            {companyLogos.primary?.title && (
              <h6>{companyLogos.primary?.title}</h6>
            )}
          </>
        )}
      </>
    );
  };

  const Certs = () => {
    return (
      <Certifications>
        <li>
          <IconCertificate />
          <span>
            ITAR Registered. ISO 9001:2015, ISO 13485:2016, AS9100D and IATF
            16949:2016 Certified.
          </span>
        </li>
      </Certifications>
    );
  };

  return hero ? (
    <>
      {breadcrumbs && breadcrumbs.length > 1 && (
        <Breadcrumbs breadcrumbs={breadcrumbs} />
      )}
      {hero.layout === Layout.ImageRight ||
      (hero.layout === Layout.ImageRight5050 && !showRfqCta) ? (
        <HeroImageRight theme={hero.theme ? hero.theme : ""}>
          <div className="container">
            <div className="grid">
              <HeroImageRightText className={textClassName}>
                <HeroTextContent />
              </HeroImageRightText>
              {hero.image?.gatsbyImageData ? (
                <HeroImageRightImage
                  className={imageClassName}
                  imageFitContain={imageFitContain}
                  image={hero.image.thumbnails?.full?.gatsbyImageData}
                  alt={hero.image.alt || "Xometry Landing Page"}
                  loading="eager"
                />
              ) : lottie.length > 0 ? (
                <HeroImageRightAnimation>
                  <Lottie options={defaultOptions} />
                </HeroImageRightAnimation>
              ) : (
                <></>
              )}
            </div>
            {(companyLogos &&
              linked_content_placement === LinkedContentPlacement.Bottom) ||
            hero.show_trustpilot ||
            hero.show_certificates ? (
              <div className="grid">
                <div
                  className={`${companyLogos.primary?.variant} company-logos-left col-span-4 md-col-span-8 lg-col-span-12 xl-col-span-6`}
                >
                  {companyLogos && <CompanyLogos />}
                </div>
                <div className="trustpilot-certs col-span-4 md-col-span-8 lg-col-span-12 xl-col-span-6">
                  {hero.show_trustpilot && <TrustBox />}
                  {hero.show_certificates && <Certs />}
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
        </HeroImageRight>
      ) : (
        <HeroBackground>
          {/* New version of Hero image. Content authored prior to the 
          refactor will not have this set. So we're checking here if 
          it's available and styled it accordingly. */}
          {hero.image?.thumbnails?.wide?.gatsbyImageData ? (
            <HeroBackgroundFull
              image={hero.image.thumbnails?.wide?.gatsbyImageData}
              alt={hero.image.alt || "Xometry Landing Page"}
              loading="eager"
            />
          ) : hero.image?.thumbnails?.full?.gatsbyImageData ? (
            <HeroBackgroundFull
              image={hero.image.thumbnails?.full?.gatsbyImageData}
              alt={hero.image.alt || "Xometry Landing Page"}
              loading="eager"
            />
          ) : hero.image?.gatsbyImageData ? (
            <HeroBackgroundDesktop
              image={hero.image?.gatsbyImageData}
              alt={hero.image?.alt || "Xometry Landing Page"}
            />
          ) : (
            <></>
          )}
          <HeroContentWrapper className="container" showRfqCta={showRfqCta}>
            <div className="grid">
              <HeroContent />
            </div>
          </HeroContentWrapper>
        </HeroBackground>
      )}
    </>
  ) : (
    <></>
  );
};
export default LandingHero;
